import { Route, Routes } from 'react-router-dom';

import { RequireAuth } from './auth/RequireAuth';
import { RequireRole } from './auth/RequireRole';
import { AppLayout } from './components/AppLayout';
import { CustomerEditPage } from './pages/CustomerEditPage';
import { CustomersListPage } from './pages/CustomersListPage';
import { LoginPage } from './pages/LoginPage';
import { RunDetailPage } from './pages/RunDetailPage';
import { RunsListPage } from './pages/RunsListPage';
import { UsersAdminPage } from './pages/UsersAdminPage';

/**
 * Top-level route table.
 *
 * `/login` is a standalone sibling route with no header chrome — a drive-by
 * visitor lands on an unbranded login form with nothing to disclose what app
 * this is. Every other route lives inside `<AppLayout>`, which renders the
 * top nav and wraps the page body in `<main>`. Gating is done per-route via
 * `<RequireAuth>` rather than at the layout level so the layout stays
 * presentation-only and the redirect semantics remain transparent in the
 * route table.
 *
 * The ADMIN-only subtrees (`/customers*`, `/users*`) are wrapped in
 * `<RequireRole role="ADMIN">`, which itself composes `<RequireAuth>` — so
 * those routes still require a session (anonymous → `/login`) AND the ADMIN
 * role (authenticated STANDARD → `/runs`). This FE guard is cosmetic; the
 * server's `requireRole` is the authoritative gate (arch §5.10).
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AppLayout />}>
        <Route
          path="/"
          element={
            <RequireAuth>
              <RunsListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/runs"
          element={
            <RequireAuth>
              <RunsListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/runs/:id"
          element={
            <RequireAuth>
              <RunDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/customers"
          element={
            <RequireRole role="ADMIN">
              <CustomersListPage />
            </RequireRole>
          }
        />
        <Route
          path="/customers/new"
          element={
            <RequireRole role="ADMIN">
              <CustomerEditPage />
            </RequireRole>
          }
        />
        <Route
          path="/customers/:id"
          element={
            <RequireRole role="ADMIN">
              <CustomerEditPage />
            </RequireRole>
          }
        />
        <Route
          path="/users"
          element={
            <RequireRole role="ADMIN">
              <UsersAdminPage />
            </RequireRole>
          }
        />
      </Route>
    </Routes>
  );
}
